import { useState } from "react";

/**
 * Componente de botões flutuantes para exportar mensagem do assistente
 * @param {Object} props
 * @param {string} props.content - Conteúdo da mensagem em markdown/texto
 * @param {string} props.messageId - ID da mensagem para nome do arquivo
 */
export default function ExportButtons({ content, messageId }) {
  const [isExporting, setIsExporting] = useState(false);

  /**
   * Converte markdown para HTML básico
   */
  function markdownToHtml(markdown) {
    let html = markdown;

    // Títulos
    html = html.replace(/^### (.*$)/gim, "<h3>$1</h3>");
    html = html.replace(/^## (.*$)/gim, "<h2>$1</h2>");
    html = html.replace(/^# (.*$)/gim, "<h1>$1</h1>");

    // Negrito
    html = html.replace(/\*\*(.*?)\*\*/g, "<strong>$1</strong>");

    // Itálico
    html = html.replace(/\*(.*?)\*/g, "<em>$1</em>");

    // Listas não ordenadas
    html = html.replace(/^\* (.*$)/gim, "<li>$1</li>");
    html = html.replace(/(<li>.*<\/li>)/s, "<ul>$1</ul>");

    // Listas ordenadas
    html = html.replace(/^\d+\. (.*$)/gim, "<li>$1</li>");

    // Quebras de linha
    html = html.replace(/\n\n/g, "</p><p>");
    html = html.replace(/\n/g, "<br>");

    // Envolver em parágrafos
    html = `<p>${html}</p>`;

    return html;
  }

  /**
   * Exporta para Word usando HTML
   */
  async function exportToWord() {
    setIsExporting(true);
    try {
      const htmlContent = markdownToHtml(content);

      // Cria documento HTML completo para Word
      const wordDocument = `
        <!DOCTYPE html>
        <html>
          <head>
            <meta charset="UTF-8">
            <title>Documento EasyVog</title>
            <style>
              body {
                font-family: 'Calibri', 'Arial', sans-serif;
                font-size: 12pt;
                line-height: 1.6;
                margin: 2cm;
              }
              h1 { font-size: 18pt; margin-top: 12pt; margin-bottom: 6pt; }
              h2 { font-size: 16pt; margin-top: 10pt; margin-bottom: 5pt; }
              h3 { font-size: 14pt; margin-top: 8pt; margin-bottom: 4pt; }
              p { margin: 6pt 0; text-align: justify; }
              ul, ol { margin: 6pt 0; padding-left: 20pt; }
              li { margin: 3pt 0; }
              strong { font-weight: bold; }
            </style>
          </head>
          <body>
            ${htmlContent}
          </body>
        </html>
      `;

      // Cria blob e faz download
      const blob = new Blob(["\ufeff", wordDocument], {
        type: "application/msword",
      });

      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `easyvog-documento-${messageId || Date.now()}.doc`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Erro ao exportar para Word:", error);
      alert("Erro ao exportar para Word. Tente novamente.");
    } finally {
      setIsExporting(false);
    }
  }

  /**
   * Exporta para PDF usando window.print
   */
  async function exportToPdf() {
    setIsExporting(true);
    try {
      const htmlContent = markdownToHtml(content);

      // Cria janela temporária para impressão
      const printWindow = window.open("", "_blank");
      if (!printWindow) {
        alert("Por favor, permita pop-ups para exportar PDF");
        setIsExporting(false);
        return;
      }

      printWindow.document.write(`
        <!DOCTYPE html>
        <html>
          <head>
            <meta charset="UTF-8">
            <title>Documento EasyVog</title>
            <style>
              @page {
                size: A4;
                margin: 2cm;
              }
              body {
                font-family: 'Arial', sans-serif;
                font-size: 12pt;
                line-height: 1.6;
                color: #000;
              }
              h1 { 
                font-size: 18pt; 
                margin-top: 12pt; 
                margin-bottom: 6pt;
                page-break-after: avoid;
              }
              h2 { 
                font-size: 16pt; 
                margin-top: 10pt; 
                margin-bottom: 5pt;
                page-break-after: avoid;
              }
              h3 { 
                font-size: 14pt; 
                margin-top: 8pt; 
                margin-bottom: 4pt;
                page-break-after: avoid;
              }
              p { 
                margin: 6pt 0; 
                text-align: justify;
                orphans: 3;
                widows: 3;
              }
              ul, ol { 
                margin: 6pt 0; 
                padding-left: 20pt; 
              }
              li { 
                margin: 3pt 0;
                page-break-inside: avoid;
              }
              strong { font-weight: bold; }
              @media print {
                body { margin: 0; }
              }
            </style>
          </head>
          <body>
            ${htmlContent}
          </body>
        </html>
      `);

      printWindow.document.close();

      // Aguarda carregamento e imprime
      printWindow.onload = function () {
        printWindow.focus();
        setTimeout(() => {
          printWindow.print();
          printWindow.close();
          setIsExporting(false);
        }, 250);
      };
    } catch (error) {
      alert("Erro ao exportar para PDF. Tente novamente.");
      setIsExporting(false);
    }
  }

  return (
    <div className="ia-export-buttons">
      <button
        className="ia-export-btn ia-export-btn--word"
        onClick={exportToWord}
        disabled={isExporting}
        title="Exportar para Word"
        aria-label="Exportar para Word"
      >
        <i className="bi bi-file-word"></i>
        <span>Word</span>
      </button>
      <button
        className="ia-export-btn ia-export-btn--pdf"
        onClick={exportToPdf}
        disabled={isExporting}
        title="Exportar para PDF"
        aria-label="Exportar para PDF"
      >
        <i className="bi bi-file-pdf"></i>
        <span>PDF</span>
      </button>
    </div>
  );
}
